import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import BackendService from "../services/BackendService";
import { clamp, avg, formatNrPlate, isValidPlate } from "../utils/plate";
import { KEYS, safeGet, safeSet, safeLoadHistory, safeSaveHistory, safeLoadJson, safeSaveJson } from "../utils/storage";

const DEFAULT_PROFILE = { name: "Rider", area: "" };
const DEFAULT_STATS = { taxisChecked: 0, ratingsGiven: 0, ratedPlates: [] };

export function normalizeRatingStats(savedStats, history = []) {
  const storedPlates = Array.isArray(savedStats?.ratedPlates) ? savedStats.ratedPlates : [];
  const historyPlates = history.filter((item) => item.stars > 0).map((item) => item.plate);
  const ratedPlates = [...new Set([...storedPlates, ...historyPlates].filter(Boolean))];
  return { ...DEFAULT_STATS, ...savedStats, ratedPlates, ratingsGiven: ratedPlates.length };
}

const AppStateContext = createContext(null);

export function AppStateProvider({ children }) {
  const navigate = useNavigate();

  const [lang, setLangState] = useState(() => safeGet(KEYS.LANG, "en"));
  const [history, setHistory] = useState(() => safeLoadHistory());
  const [profile, setProfile] = useState(() => safeLoadJson(KEYS.PROFILE, DEFAULT_PROFILE));
  const [stats, setStats] = useState(() =>
    normalizeRatingStats(safeLoadJson(KEYS.STATS, DEFAULT_STATS), history)
  );

  const [plateInput, setPlateInput] = useState("");
  const [plateValid, setPlateValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resultPlate, setResultPlate] = useState(null);

  const [welcomeOpen, setWelcomeOpen] = useState(() => safeGet(KEYS.WELCOME, "") !== "1");
  const [installOpen, setInstallOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [draftName, setDraftName] = useState("");
  const [draftArea, setDraftArea] = useState("");

  useEffect(() => safeSet(KEYS.LANG, lang), [lang]);
  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);
  useEffect(() => safeSaveHistory(history), [history]);
  useEffect(() => safeSaveJson(KEYS.PROFILE, profile), [profile]);
  useEffect(() => safeSaveJson(KEYS.STATS, stats), [stats]);

  function completeWelcome(name, area) {
    setProfile({
      name: name.trim() || DEFAULT_PROFILE.name,
      area: area.trim(),
    });
    safeSet(KEYS.WELCOME, "1");
    setWelcomeOpen(false);
    setInstallOpen(true);
  }

  async function fetchScoreForPlate(plate) {
    const res = await BackendService.getRating(plate);
    const ratings = res["ratings"];
    if (!Array.isArray(ratings) || ratings.length === 0) return null;
    return clamp(avg(ratings), 0, 5);
  }

  async function refreshAll() {
    if (!history.length) return;
    try {
      const scores = await Promise.all(
        history.map(async (h) => {
          try {
            return { plate: h.plate, score: await fetchScoreForPlate(h.plate) };
          } catch {
            return { plate: h.plate, score: undefined };
          }
        })
      );
      setHistory((prev) => {
        const byPlate = new Map(scores.map((s) => [s.plate, s.score]));
        return prev.map((item) => {
          const next = byPlate.get(item.plate);
          if (next === null || next === undefined) return item;
          return { ...item, score: next };
        });
      });
    } catch {
      // ignore — background refresh failures are silent
    }
  }

  useEffect(() => {
    refreshAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onPlateInput(value) {
    const formatted = formatNrPlate(value);
    setPlateValid(isValidPlate(formatted));
    setPlateInput(formatted);
  }

  async function check() {
    if (!isValidPlate(plateInput)) return;

    const plate = formatNrPlate(plateInput);
    setLoading(true);
    try {
      const score = await fetchScoreForPlate(plate);
      setHistory((prev) => {
        const existing = prev.find((x) => x.plate === plate);
        const nextItem = { plate, score, stars: existing?.stars ?? 0, ts: Date.now() };
        const without = prev.filter((x) => x.plate !== plate);
        return [nextItem, ...without].slice(0, 12);
      });
      setStats((prev) => ({
        ...prev,
        taxisChecked: history.some((x) => x.plate === plate) ? prev.taxisChecked : prev.taxisChecked + 1,
      }));
      setPlateInput("");
      setPlateValid(false);
      setResultPlate(plate);
    } finally {
      setLoading(false);
    }
  }

  function openTaxi(plate) {
    setResultPlate(plate);
    navigate("/");
  }

  function closeResult() {
    setResultPlate(null);
  }

  async function rate(stars) {
    if (!resultPlate) return;
    await BackendService.postRating(resultPlate, stars);
    setHistory((prev) => prev.map((x) => (x.plate === resultPlate ? { ...x, stars } : x)));
    setStats((prev) => {
      const ratedPlates = prev.ratedPlates.includes(resultPlate)
        ? prev.ratedPlates
        : [...prev.ratedPlates, resultPlate];
      return { ...prev, ratedPlates, ratingsGiven: ratedPlates.length };
    });
    await refreshAll();
  }

  function deletePlate(plate) {
    setHistory((prev) => prev.filter((x) => x.plate !== plate));
    if (resultPlate === plate) closeResult();
  }

  function toggleLangSheet() {
    setLangOpen((v) => !v);
  }

  function setLang(code) {
    setLangState(code);
    setLangOpen(false);
  }

  function openEdit() {
    setDraftName(profile.name);
    setDraftArea(profile.area);
    setEditOpen(true);
  }

  function closeEdit() {
    setEditOpen(false);
  }

  function saveEdit() {
    setProfile({
      name: draftName.trim() || DEFAULT_PROFILE.name,
      area: draftArea.trim(),
    });
    setEditOpen(false);
  }

  const resultItem = useMemo(
    () => (resultPlate ? history.find((h) => h.plate === resultPlate) || { plate: resultPlate, score: null, stars: 0 } : null),
    [history, resultPlate]
  );

  const safestPlate = useMemo(() => {
    const rated = history.filter((h) => h.stars > 0 && typeof h.score === "number");
    if (!rated.length) return null;
    return rated.reduce((best, cur) => (cur.score > best.score ? cur : best)).plate;
  }, [history]);

  const value = {
    lang,
    setLang,
    langOpen,
    toggleLangSheet,

    history,
    deletePlate,

    profile,
    stats,
    safestPlate,

    plateInput,
    plateValid,
    loading,
    onPlateInput,
    check,

    resultItem,
    openTaxi,
    closeResult,
    rate,

    welcomeOpen,
    completeWelcome,
    installOpen,
    openInstall: () => setInstallOpen(true),
    closeInstall: () => setInstallOpen(false),

    editOpen,
    draftName,
    draftArea,
    setDraftName,
    setDraftArea,
    openEdit,
    closeEdit,
    saveEdit,
  };

  return <AppStateContext.Provider value={value}>{children}</AppStateContext.Provider>;
}

export function useAppState() {
  const ctx = useContext(AppStateContext);
  if (!ctx) throw new Error("useAppState must be used within AppStateProvider");
  return ctx;
}
